import { jsx } from "slate-hyperscript";
import escapeHtml from "escape-html";
import { Text } from "slate";
import camelCase from "lodash/camelCase";
import { getStyleFromImageState, toCssValue } from "../plugins/images/Image.utils";
import { getDefaultValue } from "./index";
const styleObjectToStyleString = (styles) => Object.entries(styles)
    .map(([key, value]) => value
    ? `${key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}:${String(typeof value === "string" ? value : `${value}px`).replace(/\s/g, "")}`
    : "")
    .filter(Boolean)
    .join(";");
// format key/value of inline styles based on Slate JS data format
const formatKeyValue = (styleString) => {
    const style = styleString.split(":");
    const key = style[0].trim();
    const value = style[1].trim();
    const camelCasedKey = camelCase(key);
    return { [camelCasedKey]: toCssValue(value) };
};
const getInlineStyles = (el) => {
    const inlineStyleString = el.getAttribute("style");
    // if there are styles, camelCase them for Slate.JS
    if (inlineStyleString) {
        return inlineStyleString
            .split(";")
            .filter((x) => x)
            .reduce((restOfStyles, styleString) => ({ ...restOfStyles, ...formatKeyValue(styleString) }), {});
    }
    return {};
};
export const serialize = (node) => {
    if (Text.isText(node)) {
        let string = escapeHtml(node.text);
        // Newlines weren't rendering for some reason, so convert to BR
        string = string.replace(/\n/g, "<br>");
        if (!string) {
            return "";
        }
        const styles = [
            node.fontSize && `font-size: ${typeof node.fontSize === "number" ? `${node.fontSize}px` : node.fontSize}`,
            node.fontFamily && `font-family: ${node.fontFamily}`,
            node.color && `color: ${node.color}`
        ]
            .filter(Boolean)
            .join(";");
        const styleAttr = styles ? ` style="${styles}"` : "";
        if (node.bold) {
            string = `<strong>${string}</strong>`;
        }
        if (node.code) {
            string = `<code>${string}</code>`;
        }
        if (node.italic) {
            string = `<em>${string}</em>`;
        }
        if (node.strikeThrough) {
            string = `<del>${string}</del>`;
        }
        if (node.underline) {
            string = `<u>${string}</u>`;
        }
        return styleAttr ? `<span${styleAttr}>${string}</span>` : string;
    }
    const children = node.children.map((n) => serialize(n)).join("");
    let style = "";
    if (node.type !== "list-item" && node.type !== "image" && node.textAlign) {
        style = ` style="text-align: ${node.textAlign};"`;
    }
    if (!children && node.type !== "horizontalRule" && node.type !== "image") {
        return "";
    }
    switch (node.type) {
        case "horizontalRule":
            return `<hr${style} />`;
        case "code":
            return `<pre><code${style}>${children}</code></pre>`;
        case "quote":
            return `<blockquote${style}>${children}</blockquote>`;
        case "orderedList":
            return `<ol${style}>${children}</ol>`;
        case "unorderedList":
            return `<ul${style}>${children}</ul>`;
        case "list-item":
            return `<li${style}>${children}</li>`;
        case "list-item-text":
            return `<p${style}>${children}</p>`;
        case "headingOne":
            return `<h1${style}>${children}</h1>`;
        case "headingTwo":
            return `<h2${style}>${children}</h2>`;
        case "headingThree":
            return `<h3${style}>${children}</h3>`;
        case "headingFour":
            return `<h4${style}>${children}</h4>`;
        case "headingFive":
            return `<h5${style}>${children}</h5>`;
        case "headingSix":
            return `<h6${style}>${children}</h6>`;
        case "image": {
            const imageStyles = getStyleFromImageState(node);
            const image = `<img style="${styleObjectToStyleString(imageStyles)}" alt="${node.alt}" src="${escapeHtml(node.src)}" ${node.width ? `width="${node.width}"` : ""} ${node.height ? `height="${node.height}"` : ""}>${children}`;
            // TODO: Deprecate imageElement.href. A link element is used to wrap the image instead
            if (node.href) {
                return `<a href="${escapeHtml(node.href)}" ${node.target ? `target="${node.target}"` : ""}>${image}</a>`;
            }
            return image;
        }
        case "link":
            return `<a href="${escapeHtml(node.href)}" ${node.openInNewTab ? `target="_blank"` : ""}>${children}</a>`;
        case "paragraph":
            return `<p${style}>${children}</p>`;
        case "table":
            return `<table ${style}>${children}</table>`;
        case "table-head":
            return `<thead ${style}>${children}</thead>`;
        case "table-body":
            return `<tbody ${style}>${children}</tbody>`;
        case "table-footer":
            return `<tfoot ${style}>${children}</tfoot>`;
        case "table-header-cell":
            return `<th ${style}>${children}</th>`;
        case "table-row":
            return `<tr ${style}>${children}</tr>`;
        case "table-cell":
            return `<td ${style}>${children}</td>`;
        default:
            return children;
    }
};
export const serializeSlateValue = (value) => {
    if (!value) {
        return "";
    }
    // @ts-expect-error it's fine
    return serialize({ children: value });
};
function isElement(el) {
    return el.nodeType === 1;
}
const ELEMENT_TAGS = {
    A: (el) => ({
        type: "link",
        href: el.getAttribute("href"),
        openInNewTab: el.getAttribute("target") === "_blank"
    }),
    BLOCKQUOTE: () => ({ type: "quote" }),
    HR: () => ({ type: "horizontalRule" }),
    H1: () => ({ type: "headingOne" }),
    H2: () => ({ type: "headingTwo" }),
    H3: () => ({ type: "headingThree" }),
    H4: () => ({ type: "headingFour" }),
    H5: () => ({ type: "headingFive" }),
    H6: () => ({ type: "headingSix" }),
    IMG: (el) => {
        const style = el.getAttribute("style");
        // TODO: Parse alignState out of style
        return {
            type: "image",
            src: el.getAttribute("src"),
            alt: el.getAttribute("alt"),
            width: el.getAttribute("width"),
            height: el.getAttribute("height"),
            style
        };
    },
    LI: () => ({ type: "list-item" }),
    OL: () => ({ type: "orderedList" }),
    P: () => ({ type: "paragraph" }),
    PRE: () => ({ type: "code" }),
    UL: () => ({ type: "unorderedList" }),
    TABLE: () => ({ type: "table" }),
    THEAD: () => ({ type: "table-head" }),
    TFOOT: () => ({ type: "table-footer" }),
    TBODY: () => ({ type: "table-body" }),
    TH: () => ({ type: "table-header-cell" }),
    TR: () => ({ type: "table-row" }),
    TD: () => ({ type: "table-cell" })
};
// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS = {
    CODE: () => ({ code: true }),
    DEL: () => ({ strikeThrough: true }),
    EM: () => ({ italic: true }),
    I: () => ({ italic: true }),
    S: () => ({ strikeThrough: true }),
    B: () => ({ bold: true }),
    STRONG: () => ({ bold: true }),
    U: () => ({ underline: true }),
    SPAN: () => ({})
};
const deserializeHTMLNode = (el) => {
    if (!el) {
        return null;
    }
    if (el.nodeType === 3) {
        return el.textContent;
    }
    if (el.nodeType !== 1) {
        return null;
    }
    if (el.nodeName === "BR") {
        return jsx("fragment", {}, "\n");
    }
    // Non-element node
    if (!isElement(el)) {
        return null;
    }
    // // dump emptyNodes
    // if (typeof el.textContent === "string" && /^\s*$/g.test(el.textContent)) {
    //   return null;
    // }
    const { nodeName } = el;
    let parent = el;
    if (nodeName === "PRE" && el.childNodes[0] && el.childNodes[0].nodeName === "CODE") {
        parent = el.childNodes[0];
    }
    let children = Array.from(parent.childNodes).flatMap(deserializeHTMLNode);
    if (children.length === 0) {
        children = [{ text: "" }];
    }
    if (el.nodeName === "BODY" || (el.nodeName === "B" && el.parentNode?.nodeName === "BODY")) {
        return jsx("fragment", {}, children);
    }
    if (nodeName in ELEMENT_TAGS) {
        // eslint-disable-next-line -- TODO: real type is something along the lines of Parameters<ELEMENT_TAGS[typeof nodeName]>[0], clarify
        const attributes = ELEMENT_TAGS[nodeName](el);
        // Not sure if we actually still need this. Was done for the prezly plugin originally
        // It can maybe just be paragraph
        if (nodeName === "P" && el.parentElement?.nodeName === "LI") {
            attributes.type = "list-item-text";
        }
        // Apply state from styles
        const style = getInlineStyles(el);
        if (style.textAlign) {
            attributes.textAlign = style.textAlign;
        }
        if (nodeName === "IMG") {
            // Apply state from styles
            const { marginTop, marginBottom, marginLeft, marginRight, float } = style;
            ["borderRadius", "marginTop", "marginBottom", "marginLeft", "marginRight", "verticalAlign"].forEach((key) => {
                if (style[key]) {
                    attributes[key] = style[key];
                }
            });
            if (float === "left" || float === "right") {
                attributes.horizontalAlign = float;
            }
            else if (marginLeft === "auto" && marginRight === "auto") {
                attributes.horizontalAlign = "center";
            }
            else {
                attributes.horizontalAlign = "inline";
            }
            if ([marginTop, marginBottom, marginLeft, marginRight].every((m) => m === marginTop)) {
                attributes.isMarginLocked = true;
            }
        }
        return jsx("element", attributes, children);
    }
    if (typeof children[0] !== "string") {
        // Handles Links and Images inside a Leaf
        parent = el.childNodes[0];
    }
    if (TEXT_TAGS[nodeName]) {
        let attributes = { text: el.textContent || "" };
        const mergeAttributes = (element) => {
            const { color, fontFamily, fontSize, fontWeight, textDecoration, fontStyle } = getInlineStyles(element);
            if (color) {
                attributes.color = color;
            }
            if (fontFamily) {
                attributes.fontFamily = fontFamily;
            }
            if (fontSize) {
                attributes.fontSize = fontSize;
            }
            if (["700", "900", "bold"].includes(fontWeight)) {
                attributes.bold = true;
            }
            if (textDecoration === "underline") {
                attributes.underline = true;
            }
            if (textDecoration === "strikeThrough") {
                attributes.strikeThrough = true;
            }
            if (fontStyle === "italic") {
                attributes.italic = true;
            }
            if (TEXT_TAGS[element.nodeName]) {
                attributes = { ...attributes, ...TEXT_TAGS[element.nodeName]() };
            }
            if (element.children) {
                Array.from(element.children).forEach(mergeAttributes);
            }
        };
        mergeAttributes(el);
        return attributes;
    }
    return children;
};
export const deserializeHTMLString = (html) => {
    if (!html) {
        return getDefaultValue();
    }
    const document = new DOMParser().parseFromString(html, "text/html");
    const googleDocsBody = document.querySelector("body > b");
    const body = googleDocsBody || document.body;
    // Wrap text in a <p> if body is only text
    if (body.childElementCount === 0) {
        body.innerHTML = `<p>${body.textContent}</p>`;
    }
    return deserializeHTMLNode(googleDocsBody || document.body);
};
